import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { BookUser, CarFront, ChevronRight, Contact, ExternalLink, FileImage, FileText, IdCard, Images, RefreshCw, ScanFace, ShieldCheck, TriangleAlert } from 'lucide-react'

import { getKycQueue, kycDocumentUrl, reviewKyc, type KycQueueItem } from '../api/admin'
import { download } from '../lib/http'
import { PageHeader } from '../components/layout'
import {
  Avatar,
  Badge,
  Button,
  Callout,
  Card,
  Drawer,
  EmptyState,
  Field,
  IconButton,
  KeyValue,
  Pager,
  Panel,
  Spacer,
  Spinner,
  StatusPill,
  Table,
  TableSkeleton,
  Tabs,
  Textarea,
  cx,
  describeError,
  statusLabel,
  tableClass,
  useToast,
  when,
  type TabOption,
} from '../components/ui/Ui'
import styles from './Kyc.module.css'

type Tab = 'pending' | 'in_review' | 'resubmit' | 'approved' | 'rejected' | 'all'
type Decision = 'in_review' | 'approved' | 'rejected' | 'resubmit'

const TABS: ReadonlyArray<{ value: Tab; label: string }> = [
  { value: 'pending', label: 'Новые' },
  { value: 'in_review', label: 'На проверке' },
  { value: 'resubmit', label: 'Запрошены заново' },
  { value: 'approved', label: 'Одобрены' },
  { value: 'rejected', label: 'Отклонены' },
  { value: 'all', label: 'Все' },
]

/** The tabs that are work to do: their counts show on the tabs (page 1 is the same query the list runs there). */
const QUEUE_TABS = ['pending', 'in_review', 'resubmit'] as const

const DOC_TYPE: Record<string, string> = { passport: 'Паспорт', id_card: 'ID-карта', driver_license: 'Водительские права' }
const DOC_ICON: Record<string, ReactNode> = { passport: <BookUser />, id_card: <IdCard />, driver_license: <CarFront /> }

/** The scan kinds as the form asks for them. */
const DOC_KIND: Record<string, string> = {
  passport_main: 'Разворот с фото',
  passport_registration: 'Страница с пропиской',
  selfie_with_document: 'Селфи с документом',
  proof_of_address: 'Подтверждение адреса',
}

/** «Заявка одобрена»: the toast speaks of the application. */
const RESULT_RU: Record<Decision, string> = {
  in_review: 'взята на проверку',
  approved: 'одобрена',
  rejected: 'отклонена',
  resubmit: 'возвращена: пользователь загрузит документы заново',
}

/** What an empty tab says. */
const EMPTY_RU: Record<Tab, { title: string; description: string }> = {
  pending: { title: 'Новых заявок нет', description: 'Очередь пуста — всё поданное уже разобрано. Список обновляется сам каждые 30 секунд.' },
  in_review: { title: 'На проверке пусто', description: 'Заявки, взятые в работу, появятся здесь.' },
  resubmit: { title: 'Повторных запросов нет', description: 'Здесь ждут заявки, по которым пользователь должен загрузить документы заново.' },
  approved: { title: 'Одобренных заявок нет', description: 'Одобренные заявки появятся здесь.' },
  rejected: { title: 'Отклонённых заявок нет', description: 'Отклонённые заявки появятся здесь вместе с причиной.' },
  all: { title: 'Заявок нет', description: 'Пользователи ещё не подавали документы на верификацию.' },
}

function files(count: number): string {
  const tail = count % 100
  if (tail >= 11 && tail <= 14) return `${count} файлов`
  if (count % 10 === 1) return `${count} файл`
  if (count % 10 >= 2 && count % 10 <= 4) return `${count} файла`
  return `${count} файлов`
}

function size(bytes: number | null): string {
  if (bytes === null) return ''
  if (bytes < 1024) return `${bytes} Б`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} МБ`
}

/** How long an application has waited: «5 мин», «3 ч», «2 дн». */
function ago(iso: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000))
  if (minutes < 1) return 'только что'
  if (minutes < 60) return `${minutes} мин назад`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} ч назад`
  return `${Math.round(hours / 24)} дн назад`
}

function fullName(row: KycQueueItem): string {
  return [row.lastName, row.firstName, row.middleName].filter(Boolean).join(' ')
}

/** A date-only ISO string (`2031-05-01`) as «01.05.2031». */
function day(iso: string | null): string {
  if (!iso) return '—'
  const [y, m, d] = iso.slice(0, 10).split('-')
  return d && m && y ? `${d}.${m}.${y}` : iso
}

function expired(iso: string | null): boolean {
  if (!iso) return false
  return iso.slice(0, 10) < new Date().toISOString().slice(0, 10)
}

/** Decisions that make sense from a status; an approved application is final. */
function decisionsFor(status: string): Decision[] {
  if (status === 'approved') return []
  return (['in_review', 'resubmit', 'rejected', 'approved'] as const).filter((next) => next !== status && !(next === 'in_review' && status !== 'pending'))
}

/** Still waiting on the desk. */
function isOpen(status: string): boolean {
  return status === 'pending' || status === 'in_review'
}

interface Preview {
  id: string
  url: string
  type: string
}

/** The document type as a small chip with its icon. */
function DocType({ type }: { type: string | null }): JSX.Element {
  if (!type) return <span className={styles.muted}>—</span>
  return (
    <span className={styles.docChip}>
      <span className={styles.docChipIcon} aria-hidden="true">
        {DOC_ICON[type] ?? <IdCard />}
      </span>
      {DOC_TYPE[type] ?? type}
    </span>
  )
}

/**
 * Manual review, structure only: the operator reads the form, looks at the
 * scans right here — fetched with the operator's session, never by a public
 * link — and decides. A reason is required to reject or to ask again.
 */
export default function Kyc(): JSX.Element {
  const [tab, setTab] = useState<Tab>('pending')
  const [page, setPage] = useState(1)
  const [open, setOpen] = useState<KycQueueItem | null>(null)
  const [reason, setReason] = useState('')
  // «Отклонить» and «Запросить заново» stay at full strength: without a reason
  // the tap is answered at the field, instead of a dimmed button that says
  // nothing about the field far down the drawer.
  const [reasonMissing, setReasonMissing] = useState(false)
  const reasonInput = useRef<HTMLTextAreaElement>(null)
  // Scans fetched for the open application (object URLs) and the one shown large.
  const [scans, setScans] = useState<Record<string, Preview>>({})
  const [shown, setShown] = useState<string | null>(null)
  const [loadingDocs, setLoadingDocs] = useState<ReadonlySet<string>>(() => new Set())
  const queryClient = useQueryClient()
  const toast = useToast()

  const list = useQuery({ queryKey: ['kyc', tab, page], queryFn: () => getKycQueue(tab === 'all' ? undefined : tab, page), refetchInterval: 30_000 })
  // Page 1 of each queue tab — the very query the list runs there, so a tab switch lands on data already in hand.
  const queues = useQueries({
    queries: QUEUE_TABS.map((status) => ({ queryKey: ['kyc', status, 1], queryFn: () => getKycQueue(status, 1), refetchInterval: 30_000 })),
  })

  // A scan's object URL lives only while its application is on screen.
  const scansRef = useRef(scans)
  scansRef.current = scans
  const openIdRef = useRef<string | null>(null)
  openIdRef.current = open?.id ?? null
  const forgetScans = (): void => {
    Object.values(scansRef.current).forEach((scan) => URL.revokeObjectURL(scan.url))
    scansRef.current = {}
    setScans({})
    setShown(null)
    setLoadingDocs(new Set())
  }
  useEffect(() => () => Object.values(scansRef.current).forEach((scan) => URL.revokeObjectURL(scan.url)), [])

  const openApplication = (row: KycQueueItem): void => {
    // Every application starts from an empty reason: one typed for another person must never be sent with this decision.
    setReason('')
    setReasonMissing(false)
    forgetScans()
    openIdRef.current = row.id
    setOpen(row)
  }

  const close = (): void => {
    openIdRef.current = null
    setOpen(null)
    forgetScans()
    setReason('')
    setReasonMissing(false)
  }

  const review = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Decision }) => reviewKyc(id, status, status === 'rejected' || status === 'resubmit' ? reason.trim() : undefined),
    onSuccess: (updated, variables) => {
      toast(`Заявка ${RESULT_RU[variables.status] ?? statusLabel(updated.status)}`)
      close()
      void queryClient.invalidateQueries({ queryKey: ['kyc'] })
      void queryClient.invalidateQueries({ queryKey: ['overview'] })
    },
    onError: (error) => {
      toast(describeError(error), true)
      void queryClient.invalidateQueries({ queryKey: ['kyc'] })
    },
  })

  const showDocument = async (documentId: string, focus = true): Promise<void> => {
    if (scansRef.current[documentId]) {
      if (focus) setShown(documentId)
      return
    }
    const applicationId = openIdRef.current
    setLoadingDocs((current) => new Set(current).add(documentId))
    try {
      const blob = await download(kycDocumentUrl(documentId))
      const url = URL.createObjectURL(blob)
      // The operator may have closed or switched the application while the file was on its way.
      if (openIdRef.current !== applicationId) {
        URL.revokeObjectURL(url)
        return
      }
      scansRef.current = { ...scansRef.current, [documentId]: { id: documentId, url, type: blob.type } }
      setScans(scansRef.current)
      if (focus) setShown(documentId)
    } catch (error) {
      toast(describeError(error), true)
    } finally {
      setLoadingDocs((current) => {
        const next = new Set(current)
        next.delete(documentId)
        return next
      })
    }
  }

  const showAll = async (): Promise<void> => {
    if (!open) return
    const missing = open.documents.filter((doc) => !scansRef.current[doc.id])
    await Promise.all(missing.map((doc, i) => showDocument(doc.id, i === 0 && shown === null)))
  }

  const rows = list.data?.items ?? []
  const decisions = open ? decisionsFor(open.status) : []
  const reasonOk = reason.trim().length >= 3
  const decide = (status: Decision): void => {
    if (open === null) return
    if ((status === 'rejected' || status === 'resubmit') && !reasonOk) {
      setReasonMissing(true)
      const still = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      reasonInput.current?.focus({ preventScroll: true })
      reasonInput.current?.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'center' })
      return
    }
    review.mutate({ id: open.id, status })
  }
  const preview = shown ? scans[shown] ?? null : null
  const unloaded = open ? open.documents.filter((doc) => !scans[doc.id]).length : 0

  const tabOptions: TabOption<Tab>[] = TABS.map((option) => {
    const index = (QUEUE_TABS as readonly string[]).indexOf(option.value)
    const total = index === -1 ? undefined : queues[index]?.data?.total
    return { ...option, count: total ? total : undefined, tone: option.value === 'pending' ? 'warning' : undefined }
  })

  const onRowKey = (event: KeyboardEvent<HTMLTableRowElement>, row: KycQueueItem): void => {
    if (event.target !== event.currentTarget) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openApplication(row)
    }
  }

  return (
    <>
      <PageHeader
        title="Верификация"
        description="Анкеты и сканы документов: проверьте данные и примите решение по заявке."
        actions={
          <>
            <span className={styles.live}>
              <span className="live-dot" aria-hidden="true" />
              обновляется каждые 30 с
            </span>
            <IconButton
              icon={<RefreshCw className={list.isFetching ? 'spin' : undefined} />}
              label="Обновить сейчас"
              variant="secondary"
              onClick={() => void queryClient.invalidateQueries({ queryKey: ['kyc'] })}
            />
          </>
        }
        tabs={<Tabs variant="underline" ariaLabel="Статус заявок" value={tab} options={tabOptions} onChange={(next) => { setTab(next); setPage(1) }} />}
      />

      {list.isError ? (
        <Panel>
          <EmptyState
            tone="error"
            icon={<TriangleAlert />}
            title="Не удалось загрузить заявки"
            description={describeError(list.error)}
            action={<Button onClick={() => void list.refetch()}>Повторить</Button>}
          />
        </Panel>
      ) : list.isPending ? (
        <Panel>
          <TableSkeleton rows={5} headers={['Заявитель', 'Документ', 'Сканы', 'Статус', 'Подана']} />
        </Panel>
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState
            icon={tab === 'pending' ? <ShieldCheck /> : <Contact />}
            tone={tab === 'pending' ? 'success' : 'neutral'}
            title={EMPTY_RU[tab].title}
            description={EMPTY_RU[tab].description}
          />
        </Panel>
      ) : (
        <Panel>
          <Table ariaLabel="Заявки на верификацию" key={`${tab}-${page}`}>
            <thead>
              <tr>
                <th>Заявитель</th>
                <th>Документ</th>
                <th>Сканы</th>
                <th>Статус</th>
                <th className={tableClass.right}>Подана</th>
                <th className={tableClass.shrink}>
                  <span className="sr-only">Открыть</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const name = fullName(row)
                const who = name || row.userEmail || row.userId
                return (
                  <tr
                    key={row.id}
                    className={cx(tableClass.clickable, open?.id === row.id ? tableClass.selected : '')}
                    onClick={() => openApplication(row)}
                    onKeyDown={(event) => onRowKey(event, row)}
                    tabIndex={0}
                  >
                    <td>
                      <div className={styles.person}>
                        <Avatar name={who} size={32} />
                        <div className={styles.personText}>
                          <span className={styles.personName}>{who}</span>
                          <span className={tableClass.sub}>{name ? row.userEmail ?? row.userId : 'имя не указано'}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <DocType type={row.documentType} />
                      {row.documentNumberMasked || row.documentCountry ? (
                        <span className={tableClass.sub}>
                          {row.documentCountry ?? ''}
                          {row.documentCountry && row.documentNumberMasked ? ' · ' : ''}
                          {row.documentNumberMasked ? <span className="num">{row.documentNumberMasked}</span> : null}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      {row.documents.length > 0 ? (
                        <span className={styles.scanCount}>
                          <Images aria-hidden="true" />
                          {files(row.documents.length)}
                        </span>
                      ) : isOpen(row.status) ? (
                        <Badge tone="warning" size="sm" icon={<TriangleAlert />}>
                          нет сканов
                        </Badge>
                      ) : (
                        <span className={styles.muted}>нет</span>
                      )}
                    </td>
                    <td>
                      <StatusPill status={row.status} />
                    </td>
                    <td className={tableClass.right}>
                      <span className="num">{when(row.createdAt)}</span>
                      <span className={tableClass.sub}>{ago(row.createdAt)}</span>
                    </td>
                    <td className={cx(tableClass.shrink, styles.chevronCell)}>
                      <ChevronRight className={styles.chevron} aria-hidden="true" />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
          {list.data ? <Pager page={page} pageSize={list.data.pageSize} total={list.data.total} onPage={setPage} /> : null}
        </Panel>
      )}

      <Drawer
        open={open !== null}
        onClose={close}
        width={720}
        title={open ? fullName(open) || 'Заявка' : ''}
        subtitle={open ? `${open.userEmail ?? open.userId} · подана ${when(open.createdAt)}` : undefined}
        actions={open ? <StatusPill status={open.status} /> : null}
        footer={
          open && decisions.length > 0 ? (
            <div className={styles.decisions}>
              {decisions.includes('in_review') ? (
                <Button onClick={() => review.mutate({ id: open.id, status: 'in_review' })} loading={review.isPending}>
                  Взять на проверку
                </Button>
              ) : null}
              <Spacer />
              {decisions.includes('resubmit') ? (
                <Button variant="danger" loading={review.isPending} onClick={() => decide('resubmit')}>
                  Запросить заново
                </Button>
              ) : null}
              {decisions.includes('rejected') ? (
                <Button variant="danger" loading={review.isPending} onClick={() => decide('rejected')}>
                  Отклонить
                </Button>
              ) : null}
              {decisions.includes('approved') ? (
                <Button variant="primary" icon={<ShieldCheck />} loading={review.isPending} onClick={() => review.mutate({ id: open.id, status: 'approved' })}>
                  Одобрить
                </Button>
              ) : null}
            </div>
          ) : open ? (
            <Button variant="ghost" onClick={close}>
              Закрыть
            </Button>
          ) : null
        }
      >
        {open ? (
          <>
            {open.status === 'approved' ? (
              <Callout tone="accent" title="Заявка одобрена">
                Одобренная заявка окончательна: решение по ней больше не меняется.
              </Callout>
            ) : null}
            {open.rejectionReason ? (
              <Callout tone={open.status === 'rejected' ? 'down' : 'warning'} title={open.status === 'resubmit' ? 'Что попросили исправить' : 'Причина'}>
                {open.rejectionReason}
              </Callout>
            ) : null}

            <Card title="Анкета" icon={<Contact />} description={`Данные из формы · уровень ${open.level}`}>
              <KeyValue
                columns={2}
                labelWidth={112}
                items={[
                  { label: 'ФИО', value: fullName(open) || '—', wide: true },
                  { label: 'Документ', value: <DocType type={open.documentType} /> },
                  { label: 'Номер', value: open.documentNumberMasked ?? '—', mono: open.documentNumberMasked !== null },
                  { label: 'Страна', value: open.documentCountry ?? '—' },
                  { label: 'Дата рождения', value: day(open.dateOfBirth), mono: open.dateOfBirth !== null },
                  { label: 'Выдан', value: day(open.documentIssueDate), mono: open.documentIssueDate !== null },
                  {
                    label: 'Действует до',
                    value: (
                      <span className={styles.inline}>
                        <span className={open.documentExpiryDate ? 'num' : undefined}>{day(open.documentExpiryDate)}</span>
                        {expired(open.documentExpiryDate) ? (
                          <Badge tone="down" size="sm">
                            срок истёк
                          </Badge>
                        ) : null}
                      </span>
                    ),
                  },
                  { label: 'Проверена', value: when(open.reviewedAt), mono: open.reviewedAt !== null, wide: true },
                ]}
              />
            </Card>

            <Card
              title="Сканы"
              icon={<Images />}
              description={open.documents.length === 0 ? 'Файлы не загружены' : `${files(open.documents.length)} · открываются только с вашей сессией`}
              actions={
                unloaded > 1 ? (
                  <Button size="sm" variant="ghost" icon={<FileImage />} onClick={() => void showAll()}>
                    Показать все
                  </Button>
                ) : null
              }
            >
              {open.documents.length === 0 ? (
                <Callout tone="warning" title="Без сканов заявку не одобряют">
                  Запросите документы заново и укажите причину — пользователь увидит её в приложении.
                </Callout>
              ) : (
                <div className={styles.scanGrid}>
                  {open.documents.map((doc) => {
                    const scan = scans[doc.id]
                    const busy = loadingDocs.has(doc.id)
                    const mime = scan?.type || doc.mimeType || ''
                    return (
                      <button
                        key={doc.id}
                        type="button"
                        className={styles.scan}
                        data-active={shown === doc.id || undefined}
                        aria-pressed={shown === doc.id}
                        aria-busy={busy || undefined}
                        onClick={() => void showDocument(doc.id)}
                      >
                        <span className={styles.thumb}>
                          {scan && scan.type.startsWith('image/') ? (
                            <img src={scan.url} alt="" className={styles.thumbImage} />
                          ) : (
                            <span className={styles.thumbIcon} aria-hidden="true">
                              {doc.kind === 'selfie_with_document' ? <ScanFace /> : mime.includes('pdf') ? <FileText /> : <FileImage />}
                            </span>
                          )}
                          {busy ? (
                            <span className={styles.thumbBusy}>
                              <Spinner size={18} label="Загружаем скан" />
                            </span>
                          ) : !scan ? (
                            <span className={styles.thumbHint}>Показать</span>
                          ) : null}
                        </span>
                        <span className={styles.scanText}>
                          <span className={styles.scanKind}>{DOC_KIND[doc.kind] ?? doc.kind}</span>
                          <span className={styles.scanMeta}>
                            {[size(doc.sizeBytes), mime.replace(/^(image|application)\//, '').toUpperCase()].filter(Boolean).join(' · ') || 'файл'}
                          </span>
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}
              {preview ? (
                <div className={styles.preview} key={preview.id}>
                  <div className={styles.previewBar}>
                    <span className={styles.previewTitle}>{DOC_KIND[open.documents.find((doc) => doc.id === preview.id)?.kind ?? ''] ?? 'Скан'}</span>
                    <a href={preview.url} target="_blank" rel="noreferrer" className={styles.openLink}>
                      <ExternalLink aria-hidden="true" />
                      Открыть в новой вкладке
                    </a>
                  </div>
                  {preview.type.startsWith('image/') ? (
                    <img src={preview.url} alt="Скан документа" className={styles.image} />
                  ) : preview.type === 'application/pdf' ? (
                    <object data={preview.url} type="application/pdf" className={styles.pdf} aria-label="Скан документа (PDF)">
                      <span className={styles.muted}>PDF не показывается в окне — откройте его ссылкой выше.</span>
                    </object>
                  ) : (
                    <span className={styles.muted}>Этот тип файла не показывается в окне — откройте его ссылкой выше.</span>
                  )}
                </div>
              ) : null}
            </Card>

            {decisions.includes('rejected') || decisions.includes('resubmit') ? (
              <Field
                label="Причина отказа или повторной подачи"
                aside={reason.trim().length > 0 ? `${reason.trim().length} симв.` : 'от 3 символов'}
                hint="Обязательна, чтобы отклонить или запросить заново; пользователь увидит её в приложении."
                error={reasonMissing && !reasonOk ? 'Напишите причину, от 3 символов: без неё заявку не отклонить и не вернуть. Пользователь увидит её в приложении.' : null}
              >
                <Textarea ref={reasonInput} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Например: скан размыт, не читается номер документа" />
              </Field>
            ) : null}
          </>
        ) : null}
      </Drawer>
    </>
  )
}
